import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Tooltip, Typography } from "@mui/material";
import type { Moment } from "moment";
import type {
	GasConsumptionMillRolling,
	GasConsumptionMiniBucket,
	GasConsumptionSummary,
} from "../../../shared/consumption/models/GasConsumption";
import {
	formatCostAmount,
	formatCurrency,
	formatMeasureWithUnit,
	formatVolume,
	GAS_RATIO_UNIT,
	GAS_UNIT,
} from "../../../shared/consumption/utils/format";
import { toUtilityMiniBuckets } from "../../../shared/consumption/utils/gasConsumption";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_FOCUS_RING_SX,
} from "../../../shared/consumption/utils/tokens";
import { ConsumptionMetric } from "../ConsumptionMetric";
import { ConsumptionCard, metricRowSx } from "../at-a-glance/ConsumptionCard";
import { getTrendProperties } from "../at-a-glance/ConsumptionTrendCard";
import {
	formatBillPeriod,
	formatCostPerTon,
	formatRollingInstant,
	formatRollingWindow,
	renderCostLine,
} from "../at-a-glance/RollingEfficiencyCard";
import { PairedDaysChart } from "../at-a-glance/PairedDaysChart";

const moment = getMomentTz();

const formatThousands = (value: number | null) => {
	if (value === null) return null;
	if (Math.abs(value) < 100_000) return formatVolume(value);
	return `${(value / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}k`;
};

const getPartialHistoryNote = (
	summary: GasConsumptionSummary,
	periodStart: Moment,
) =>
	summary.DataSinceUtc !== null &&
	moment(summary.DataSinceUtc).isAfter(periodStart)
		? `Recorded since ${moment(summary.DataSinceUtc).format("MMM D, YYYY")}`
		: undefined;

const markApproximate = (value: string | null, isApproximate: boolean) =>
	value !== null && isApproximate ? `≈${value}` : value;

const withApproximateHeatNote = (tooltip: string, isApproximate: boolean) =>
	isApproximate
		? `${tooltip} · Part of these days have no earlier gas bill, so they use the heat value of the nearest later one.`
		: tooltip;

const getHeatNote = (
	isPartial: boolean,
	isApproximate: boolean,
	value: number | null,
) => {
	if (value === null) return null;
	if (isPartial) return "Partial";
	return isApproximate ? "Approximate" : null;
};

const getHeatTooltip = (
	isPartial: boolean,
	isApproximate: boolean,
	value: number | null,
	tooltip: string | undefined,
) => {
	if (isPartial && value === null)
		return "No heat value yet: no gas bill covers these days.";
	const note = isPartial
		? "Leaves out the days no gas bill covers yet, which have no heat value."
		: isApproximate && value !== null
			? "Part of these days have no earlier gas bill, so they use the heat value of the nearest later one."
			: undefined;
	if (note === undefined) return tooltip;
	return tooltip === undefined ? note : `${tooltip} · ${note}`;
};

const GasTrendCard = ({ summary }: { summary: GasConsumptionSummary }) => {
	const trend = getTrendProperties(summary.SevenDayChangePercent);

	return (
		<ConsumptionCard
			area="comparison"
			label="Seven day gas trend"
			upper={
				<Box
					sx={{
						alignItems: "flex-end",
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						justifyContent: "space-between",
					}}
				>
					<ConsumptionMetric
						label="Last 7 days"
						lead
						value={formatVolume(summary.Last7Mmbtu)}
						unit={GAS_UNIT}
						secondary={getHeatNote(
							summary.IsLast7MmbtuPartial,
							summary.IsLast7MmbtuApproximate,
							summary.Last7Mmbtu,
						)}
						tooltip={getHeatTooltip(
							summary.IsLast7MmbtuPartial,
							summary.IsLast7MmbtuApproximate,
							summary.Last7Mmbtu,
							formatMeasureWithUnit(summary.Last7Mmbtu, GAS_UNIT),
						)}
					/>
					<Tooltip describeChild title={trend.tooltip}>
						<Box
							tabIndex={0}
							sx={{
								alignItems: "flex-end",
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
								outline: "none",
								textAlign: "right",
								"&:focus-visible": CONSUMPTION_FOCUS_RING_SX,
							}}
						>
							<Box
								sx={{
									alignItems: "center",
									color: trend.color,
									display: "flex",
									gap: 0.5,
								}}
							>
								<trend.Icon aria-hidden="true" sx={{ fontSize: "1.5rem" }} />
								<Typography
									aria-hidden="true"
									sx={{
										fontSize: "1.5rem",
										fontVariantNumeric: "tabular-nums",
										fontWeight: 600,
										lineHeight: 1,
									}}
								>
									{trend.percentage}
								</Typography>
							</Box>
							<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
								vs previous 7 days
							</Typography>
						</Box>
					</Tooltip>
				</Box>
			}
			lower={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="Previous 7 days"
						value={formatVolume(summary.Previous7Mmbtu)}
						unit={GAS_UNIT}
						secondary={getHeatNote(
							summary.IsPrevious7MmbtuPartial,
							summary.IsPrevious7MmbtuApproximate,
							summary.Previous7Mmbtu,
						)}
						tooltip={getHeatTooltip(
							summary.IsPrevious7MmbtuPartial,
							summary.IsPrevious7MmbtuApproximate,
							summary.Previous7Mmbtu,
							undefined,
						)}
					/>
					<ConsumptionMetric
						label="30-day daily average"
						value={formatVolume(summary.ThirtyDayAverageMmbtu)}
						unit={`${GAS_UNIT}/day`}
						secondary={getHeatNote(
							summary.IsThirtyDayAverageMmbtuPartial,
							summary.IsThirtyDayAverageMmbtuApproximate,
							summary.ThirtyDayAverageMmbtu,
						)}
						tooltip={
							summary.ThirtyDayAverageMmbtu === null &&
							!summary.IsThirtyDayAverageMmbtuPartial
								? "Needs thirty complete days of furnace gas."
								: getHeatTooltip(
										summary.IsThirtyDayAverageMmbtuPartial,
										summary.IsThirtyDayAverageMmbtuApproximate,
										summary.ThirtyDayAverageMmbtu,
										undefined,
									)
						}
					/>
				</Box>
			}
		/>
	);
};

const GasTotalsCard = ({ summary }: { summary: GasConsumptionSummary }) => {
	const monthStart = moment().startOf("month");
	const yearStart = moment().startOf("year");
	const monthToDateNote = getPartialHistoryNote(summary, monthStart);
	const lastMonthNote = getPartialHistoryNote(
		summary,
		monthStart.clone().subtract(1, "month"),
	);
	const yearToDateNote = getPartialHistoryNote(summary, yearStart);

	return (
		<ConsumptionCard
			area="totals"
			label="Monthly and annual gas totals"
			upper={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label={`Total to date · ${summary.CurrentMonthLabel}`}
						value={formatThousands(summary.MonthToDateMmbtu)}
						unit={GAS_UNIT}
						secondary={joinSecondary(
							monthToDateNote ?? null,
							getHeatNote(
								summary.IsMonthToDateMmbtuPartial,
								summary.IsMonthToDateMmbtuApproximate,
								summary.MonthToDateMmbtu,
							),
						)}
						tooltip={getHeatTooltip(
							summary.IsMonthToDateMmbtuPartial,
							summary.IsMonthToDateMmbtuApproximate,
							summary.MonthToDateMmbtu,
							formatMeasureWithUnit(summary.MonthToDateMmbtu, GAS_UNIT),
						)}
					/>
					<ConsumptionMetric
						label={`Last month total · ${summary.PreviousMonthLabel}`}
						value={formatThousands(summary.LastMonthTotalMmbtu)}
						unit={GAS_UNIT}
						secondary={joinSecondary(
							lastMonthNote ?? null,
							getHeatNote(
								summary.IsLastMonthMmbtuPartial,
								summary.IsLastMonthMmbtuApproximate,
								summary.LastMonthTotalMmbtu,
							),
						)}
						tooltip={getHeatTooltip(
							summary.IsLastMonthMmbtuPartial,
							summary.IsLastMonthMmbtuApproximate,
							summary.LastMonthTotalMmbtu,
							formatMeasureWithUnit(summary.LastMonthTotalMmbtu, GAS_UNIT),
						)}
					/>
				</Box>
			}
			lower={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="Total · YTD"
						value={formatThousands(summary.YearToDateMmbtu)}
						unit={GAS_UNIT}
						secondary={joinSecondary(
							yearToDateNote ?? null,
							getHeatNote(
								summary.IsYearToDateMmbtuPartial,
								summary.IsYearToDateMmbtuApproximate,
								summary.YearToDateMmbtu,
							),
						)}
						tooltip={getHeatTooltip(
							summary.IsYearToDateMmbtuPartial,
							summary.IsYearToDateMmbtuApproximate,
							summary.YearToDateMmbtu,
							formatMeasureWithUnit(summary.YearToDateMmbtu, GAS_UNIT),
						)}
					/>
					<ConsumptionMetric
						label="Avg per Month"
						value={formatThousands(summary.AverageMonthMmbtu)}
						unit={GAS_UNIT}
						secondary={getHeatNote(
							summary.IsAverageMonthMmbtuPartial,
							summary.IsAverageMonthMmbtuApproximate,
							summary.AverageMonthMmbtu,
						)}
						tooltip={getHeatTooltip(
							summary.IsAverageMonthMmbtuPartial,
							summary.IsAverageMonthMmbtuApproximate,
							summary.AverageMonthMmbtu,
							summary.AverageMonthMmbtu === null
								? "No furnace gas has been recorded this year yet."
								: `${formatMeasureWithUnit(summary.AverageMonthMmbtu, GAS_UNIT)} · this year's gas over the ${summary.AverageMonthCount.toLocaleString("en-US", { maximumFractionDigits: 1 })} months the readings cover, counting a month measured in part as the part it covers.`,
						)}
					/>
				</Box>
			}
		/>
	);
};

const joinSecondary = (...parts: (string | null)[]) => {
	const shown = parts.filter((part): part is string => part !== null);
	return shown.length === 0 ? null : shown.join(" · ");
};

const GasCostRow = ({ summary }: { summary: GasConsumptionSummary }) => {
	const hasCost = summary.MainTotalCost !== null;
	const billPeriod = formatBillPeriod(
		summary.MainCostBillStart,
		summary.MainCostBillEnd,
	);
	const approximateHint = summary.IsMainCostApproximate
		? " Part of the period has no bill of its own, so it is priced with the nearest bill."
		: "";

	return (
		<Box sx={metricRowSx}>
			<ConsumptionMetric
				label="Cost of this period"
				value={formatCurrency(summary.MainTotalCost)}
				secondary={
					hasCost && summary.IsMainCostApproximate ? "Approximate" : null
				}
				tooltip={
					hasCost
						? `Furnace gas priced with the gas bills, the latest of them for ${billPeriod}.${approximateHint}`
						: "No gas bill entered yet to price this period with."
				}
			/>
			<ConsumptionMetric
				label="Cost per MCF"
				value={formatCostAmount(summary.MainCostPerMcf)}
				unit="/MCF"
				tooltip={
					hasCost
						? `What one MCF of this period cost.${approximateHint}`
						: "No gas bill entered yet to price this period with."
				}
			/>
		</Box>
	);
};

const GasPeriodCard = ({
	summary,
	periodLabel,
}: {
	summary: GasConsumptionSummary;
	periodLabel: string;
}) => (
	<ConsumptionCard
		area="efficiency"
		label="Gas per ton rolled"
		title={periodLabel}
		upper={
			<Box sx={metricRowSx}>
				<ConsumptionMetric
					label="Gas per ton"
					lead
					value={markApproximate(
						formatVolume(summary.MainMmbtuPerTon),
						summary.IsMainMmbtuApproximate,
					)}
					unit={GAS_RATIO_UNIT}
					secondary={joinSecondary(
						formatCostPerTon(
							summary.MainCostPerTon,
							summary.IsMainCostApproximate,
						),
						summary.IsMainPartial ? "Period still open" : null,
					)}
					tooltip={withApproximateHeatNote(
						"Furnace gas over the tons rolled, counting only the periods that already have both.",
						summary.IsMainMmbtuApproximate,
					)}
				/>
				<ConsumptionMetric
					label="Tons rolled"
					value={formatVolume(summary.MainTotalTons)}
					unit="tons"
				/>
			</Box>
		}
		lower={
			<Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="Furnace gas"
						value={formatThousands(summary.MainTotalMmbtu)}
						unit={GAS_UNIT}
						secondary={getHeatNote(
							summary.IsMainMmbtuPartial,
							summary.IsMainMmbtuApproximate,
							summary.MainTotalMmbtu,
						)}
						tooltip={getHeatTooltip(
							summary.IsMainMmbtuPartial,
							summary.IsMainMmbtuApproximate,
							summary.MainTotalMmbtu,
							formatMeasureWithUnit(summary.MainTotalMmbtu, GAS_UNIT),
						)}
					/>
					<ConsumptionMetric
						label="Last 24 hours"
						value={formatVolume(summary.Rolling24HourMmbtu)}
						unit={GAS_UNIT}
						secondary={getHeatNote(
							summary.IsRolling24HourMmbtuPartial,
							summary.IsRolling24HourMmbtuApproximate,
							summary.Rolling24HourMmbtu,
						)}
						tooltip={getHeatTooltip(
							summary.IsRolling24HourMmbtuPartial,
							summary.IsRolling24HourMmbtuApproximate,
							summary.Rolling24HourMmbtu,
							undefined,
						)}
					/>
				</Box>
				<GasCostRow summary={summary} />
			</Box>
		}
	/>
);

const GasRollingCard = ({
	summary,
	millRolling,
}: {
	summary: GasConsumptionSummary;
	millRolling: GasConsumptionMillRolling[];
}) => {
	const currentRollingStartUtc = millRolling[0]?.CurrentRollingStartUtc ?? null;
	const currentRollingWindow = formatRollingWindow(
		currentRollingStartUtc,
		summary.DataThroughUtc,
	);
	const isCurrentRollingUnmeasured =
		currentRollingStartUtc !== null &&
		summary.DataThroughUtc !== null &&
		currentRollingStartUtc.getTime() >= summary.DataThroughUtc.getTime();
	const hasRate = summary.CurrentRollingCostPerMcf !== null;
	const billPeriod = formatBillPeriod(
		summary.CurrentRollingCostBillStart,
		summary.CurrentRollingCostBillEnd,
	);

	const costNote = isCurrentRollingUnmeasured
		? null
		: !hasRate
			? "No gas bill entered yet."
			: summary.IsCurrentRollingCostApproximate
				? "Approximate: part of this run has no bill of its own, so it is priced with the nearest bill."
				: null;

	return (
		<ConsumptionCard
			area="efficiency"
			label="Rolling gas efficiency"
			title={`Section · ${summary.CurrentRollingSection ?? "N/A"}`}
			upper={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="This rolling"
						lead
						value={markApproximate(
							formatVolume(summary.CurrentRollingMmbtuPerTon),
							summary.IsCurrentRollingMmbtuApproximate,
						)}
						unit={GAS_RATIO_UNIT}
						secondary={renderCostLine({
							costPerTon: summary.CurrentRollingCostPerTon,
							isApproximate: summary.IsCurrentRollingCostApproximate,
							trailing: isCurrentRollingUnmeasured
								? `Started ${formatRollingInstant(currentRollingStartUtc)}`
								: null,
						})}
						tooltip={
							isCurrentRollingUnmeasured
								? `This run started ${formatRollingInstant(currentRollingStartUtc)}, and the furnace has only reported through ${formatRollingInstant(summary.DataThroughUtc)}. Nothing has been measured for it yet.`
								: withApproximateHeatNote(
										`Furnace gas over the tons rolled during the current run of this section${
											currentRollingWindow === null
												? ""
												: `, ${currentRollingWindow}`
										}.`,
										summary.IsCurrentRollingMmbtuApproximate,
									)
						}
					/>
					<ConsumptionMetric
						label="Previous rolling"
						value={markApproximate(
							formatVolume(summary.PreviousRollingMmbtuPerTon),
							summary.IsPreviousRollingMmbtuApproximate,
						)}
						unit={GAS_RATIO_UNIT}
						secondary={renderCostLine({
							costPerTon: summary.PreviousRollingCostPerTon,
							isApproximate: summary.IsPreviousRollingCostApproximate,
							trailing: formatRollingWindow(
								summary.PreviousRollingStartUtc,
								summary.PreviousRollingEndUtc,
							),
						})}
						tooltip={
							summary.PreviousRollingSection === null
								? "No earlier completed run of this section was found."
								: withApproximateHeatNote(
										"Furnace gas over the tons rolled the last time this section was rolled.",
										summary.IsPreviousRollingMmbtuApproximate,
									)
						}
					/>
				</Box>
			}
			lower={
				<Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
					<Box sx={metricRowSx}>
						<ConsumptionMetric
							label="Cost per MCF"
							testId="gas-rolling-cost-per-mcf"
							value={formatCostAmount(summary.CurrentRollingCostPerMcf)}
							unit="/MCF"
							tooltip={
								!hasRate
									? "No gas bill entered yet."
									: summary.IsCurrentRollingCostApproximate
										? `What one MCF cost. Part of the run has no bill of its own, so it is priced with the nearest bill; the latest one used is for ${billPeriod}.`
										: `What one MCF cost, from the bill of ${billPeriod}.`
							}
						/>
					</Box>
					{costNote !== null && (
						<Typography
							component="div"
							data-test-id="gas-rolling-cost-note"
							sx={{ ...CONSUMPTION_CAPTION_SX, color: "#767F89" }}
						>
							{costNote}
						</Typography>
					)}
				</Box>
			}
		/>
	);
};

const GasIntensityCard = ({
	summary,
	millRolling,
	periodLabel,
}: {
	summary: GasConsumptionSummary;
	millRolling: GasConsumptionMillRolling[];
	periodLabel: string;
}) =>
	millRolling.length === 1 && summary.CurrentRollingSection !== null ? (
		<GasRollingCard summary={summary} millRolling={millRolling} />
	) : (
		<GasPeriodCard summary={summary} periodLabel={periodLabel} />
	);

interface GasAtAGlanceProps {
	summary: GasConsumptionSummary;
	millRolling: GasConsumptionMillRolling[];
	buckets: GasConsumptionMiniBucket[];
	periodLabel: string;
}

export const GasAtAGlance = ({
	summary,
	millRolling,
	buckets,
	periodLabel,
}: GasAtAGlanceProps) => (
	<Box
		component="section"
		aria-label="Gas at a Glance"
		sx={{ containerType: "inline-size", minWidth: 0 }}
	>
		<Box
			sx={{
				display: "grid",
				gridTemplateAreas: '"comparison" "totals" "efficiency" "chart"',
				gridTemplateColumns: "minmax(0, 1fr)",
				gap: 1,
				minWidth: 0,
				maxWidth: "100%",
				"@container (min-width: 600px)": {
					gridTemplateAreas:
						'"comparison comparison" "totals efficiency" "chart chart"',
					gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
				},
				"@container (min-width: 1400px)": {
					gridTemplateAreas: '"comparison totals efficiency chart"',
					gridTemplateColumns:
						"minmax(340px, 1.4fr) repeat(2, minmax(320px, 1.5fr)) minmax(400px, 2fr)",
				},
			}}
		>
			<GasTrendCard summary={summary} />
			<GasTotalsCard summary={summary} />
			<GasIntensityCard
				summary={summary}
				millRolling={millRolling}
				periodLabel={periodLabel}
			/>
			<PairedDaysChart
				buckets={toUtilityMiniBuckets(buckets)}
				averageGallons={summary.ThirtyDayAverageMmbtu}
				unit={GAS_UNIT}
				measureName={GAS_UNIT}
				subject="furnace gas"
			/>
		</Box>
	</Box>
);
